import React from 'react';
import { ProcessValidationResult } from '../../types/processDefinition';
import './ProcessValidationPanel.css';

interface ProcessValidationPanelProps {
  validationResults: ProcessValidationResult | null;
  onRefresh: () => void;
}

export const ProcessValidationPanel: React.FC<ProcessValidationPanelProps> = ({
  validationResults,
  onRefresh,
}) => {
  if (!validationResults) {
    return (
      <div className="process-validation-panel">
        <h3>Process Validation</h3>
        <p>No validation data available.</p>
        <button onClick={onRefresh}>Refresh</button>
      </div>
    );
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'ERROR':
        return '❌';
      case 'WARNING':
        return '⚠️';
      case 'INFO':
        return 'ℹ️';
      default:
        return '❓';
    }
  };

  const getSeverityClass = (severity: string) => {
    switch (severity) {
      case 'ERROR':
        return 'error';
      case 'WARNING':
        return 'warning';
      case 'INFO':
        return 'info';
      default:
        return 'unknown';
    }
  };

  return (
    <div className="process-validation-panel">
      <div className="validation-header">
        <h3>Process Validation</h3>
        <button onClick={onRefresh} className="refresh-btn">
          🔄
        </button>
      </div>

      <div className="validation-summary">
        <div className="summary-item">
          <span className="label">Total Issues:</span>
          <span className="value">{validationResults.total_issues}</span>
        </div>
        <div className="summary-item error">
          <span className="label">Errors:</span>
          <span className="value">{validationResults.error_count}</span>
        </div>
        <div className="summary-item warning">
          <span className="label">Warnings:</span>
          <span className="value">{validationResults.warning_count}</span>
        </div>
        <div className="summary-item info">
          <span className="label">Info:</span>
          <span className="value">{validationResults.info_count}</span>
        </div>
      </div>

      <div className="validation-issues">
        {validationResults.validation_results.map((issue, index) => (
          <div key={index} className={`validation-issue ${getSeverityClass(issue.severity)}`}>
            <div className="issue-header">
              <span className="severity-icon">{getSeverityIcon(issue.severity)}</span>
              <span className="issue-type">{issue.validation_type}</span>
            </div>
            <div className="issue-message">{issue.message}</div>
            {issue.artifact_id && (
              <div className="issue-artifact">
                <span className="label">Artifact:</span>
                <span className="value">{issue.artifact_id}</span>
              </div>
            )}
            {issue.fixable && issue.suggested_fix && (
              <div className="suggested-fix">
                <div className="fix-description">{issue.suggested_fix.description}</div>
                {issue.suggested_fix.action && <button className="apply-fix-btn">Apply Fix</button>}
              </div>
            )}
          </div>
        ))}
      </div>

      {validationResults.total_issues === 0 && (
        <div className="validation-success">
          <span className="success-icon">✅</span>
          <p>All validations passed! Your process is properly configured.</p>
        </div>
      )}
    </div>
  );
};
